import React, { useState, useEffect } from 'react';
import { Box, Text, Group, Stack, Paper, Title, ActionIcon, TextInput, Button, Transition, Modal, Badge } from '@mantine/core';
import { IconTrash, IconPlus, IconArrowRight, IconCircleCheck } from '@tabler/icons-react';
import { v4 as uuidv4 } from 'uuid';
import CompletedScenarioCard from './CompletedScenarioCard';

export default function ScenarioBuilder({
  scenarios,
  onAddScenario,
  onRemoveScenario,
  onStartEvaluation,
  completedScenarios,
  securityControls,
  onResumeScenario,
}) {
  const [scenarioName, setScenarioName] = useState('');
  const [showNameInput, setShowNameInput] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [statusFilter, setStatusFilter] = useState(() => {
    try { return localStorage.getItem('secScenario.status') || 'all'; } catch { return 'all'; }
  });
  const [sortBy, setSortBy] = useState(() => {
    try { return localStorage.getItem('secScenario.sort') || 'updated'; } catch { return 'updated'; }
  });
  useEffect(() => { try { localStorage.setItem('secScenario.status', statusFilter); } catch {} }, [statusFilter]);
  useEffect(() => { try { localStorage.setItem('secScenario.sort', sortBy); } catch {} }, [sortBy]);

  const handleOpenNameInput = () => {
    // Immediately create a placeholder scenario and jump to evaluation
    const placeholderIndex = (Array.isArray(scenarios) ? scenarios.length : 0) + 1;
    const scenarioToAdd = {
      id: uuidv4(),
      name: `Scenario ${placeholderIndex}`,
      attributes: {},
      mappedAgents: [],
    };
    onAddScenario(scenarioToAdd);
    onStartEvaluation?.(scenarioToAdd);
  };

  const handleNameSubmit = () => {
    const name = scenarioName.trim();
    if (!name) return;
    const scenarioToAdd = {
      id: uuidv4(),
      name,
      attributes: {},
      mappedAgents: [],
    };
    onAddScenario(scenarioToAdd);
    onStartEvaluation?.(scenarioToAdd);
    setScenarioName('');
    setShowNameInput(false);
  };

  const fullList = (scenarios || []).map(s => ({
    ...s,
    status: s.status || (completedScenarios.find(c => c.id === s.id) ? 'completed' : 'active'),
  }));
  const filtered = fullList.filter(s => statusFilter === 'all' ? true : s.status === statusFilter);
  const filteredDedup = Array.from(new Map(filtered.map(s => [s.id, s])).values());
  const completedDedup = Array.from(new Map(fullList.filter(s => s.status==='completed').map(s => [s.id, s])).values());
  const activeScenarios = [...filteredDedup].sort((a, b) => {
    if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
    if (sortBy === 'progress') {
      const score = (x) => {
        const attrs = Object.keys(x.attributes || {}).length;
        const agents = Array.isArray(x.mappedAgents) ? x.mappedAgents.length : 0;
        const attack = Object.keys(x.attackSurface || {}).length;
        const impact = Object.keys(x.impact || {}).length;
        const controls = (securityControls?.[x.id] || []).filter((c) => ['saved','updated'].includes((c.status||'').toLowerCase())).length;
        return attrs + agents + attack + impact + controls;
      };
      return score(b) - score(a);
    }
    // default: updated desc
    return new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime();
  });

  return (
    <Stack gap="lg">
      <Title order={4} c="dark.7">Threat scenarios</Title>

      {/* Inline naming first */}
      {showNameInput && (
        <Transition mounted={showNameInput} transition="slide-down" duration={250} timingFunction="ease">
          {(styles) => (
            <Paper withBorder radius="md" p="lg" sx={(t) => ({ backgroundColor: t.white })} style={styles}>
              <Title order={5} c="dark.7" mb="md">Scenario name</Title>
              <Text size="sm" c="dimmed" mb="lg">Give this scenario a clear, descriptive name</Text>
              <TextInput
                placeholder="e.g., APT via model poisoning"
                value={scenarioName}
                onChange={(e) => setScenarioName(e.currentTarget.value)}
                size="md" mb="lg"
                autoFocus
              />
              <Group justify="flex-end">
                <Button size="md" rightSection={<IconArrowRight size={16} />} onClick={handleNameSubmit}>
                  {Array.isArray(scenarios) && scenarios.length === 0 ? 'Start threat model' : 'Add scenario'}
                </Button>
              </Group>
            </Paper>
          )}
        </Transition>
      )}

      {/* Controls */}
      <Group justify="space-between" align="center">
        <Group gap="xs">
          <Text size="xs" c="dimmed">Status</Text>
          <Button variant="subtle" size="xs" onClick={() => setStatusFilter('all')} disabled={statusFilter==='all'}>All</Button>
          <Button variant="subtle" size="xs" onClick={() => setStatusFilter('active')} disabled={statusFilter==='active'}>
            Active <Badge ml={6} size="xs" color="blue" variant="light">{fullList.filter(s=>s.status==='active').length}</Badge>
          </Button>
          <Button variant="subtle" size="xs" onClick={() => setStatusFilter('completed')} disabled={statusFilter==='completed'}>
            Completed <Badge ml={6} size="xs" color="green" variant="light">{fullList.filter(s=>s.status==='completed').length}</Badge>
          </Button>
        </Group>
        <Group gap="xs">
          <Text size="xs" c="dimmed">Sort by</Text>
          <Button variant="subtle" size="xs" onClick={() => setSortBy('updated')} disabled={sortBy==='updated'}>Updated</Button>
          <Button variant="subtle" size="xs" onClick={() => setSortBy('progress')} disabled={sortBy==='progress'}>Progress</Button>
        </Group>
      </Group>

      {/* Unified list for All/Active filters */}
      {statusFilter !== 'completed' && activeScenarios.length > 0 && (
        <Stack gap="md">
          {activeScenarios.map((scenario) => (
            <Paper
              key={scenario.id}
              withBorder p="lg" radius="md"
              sx={(t) => ({ borderColor: t.colors.blue[4], backgroundColor: t.white, borderWidth: 2, transition: 'all 0.2s ease', '&:hover': { boxShadow: t.shadows.sm } })}
            >
              <Group justify="space-between" mb="md">
                <Group gap="sm">
                  <IconCircleCheck size={18} style={{ color: '#adb5bd' }} />
                  <Box>
                    <Text size="md" fw={600} c="dark.7">{scenario.name}</Text>
                    {scenario.updatedAt && (
                      <Text size="xs" c="dimmed">Updated {new Date(scenario.updatedAt).toLocaleString()}</Text>
                    )}
                  </Box>
                </Group>
                <Group gap="xs">
                  {/* badges */}
                  <Group gap={6}>
                    <Badge variant="light" size="xs" color={Object.keys(scenario.attributes || {}).length>0?'gray':'gray'} onClick={() => onResumeScenario?.({ id: scenario.id, phase: 'attributes' })} sx={{ cursor: 'pointer' }}>
                      {Object.keys(scenario.attributes || {}).length>0 ? 'Attributes ✓' : 'Attributes'}
                    </Badge>
                    <Badge variant="light" size="xs" color={Array.isArray(scenario.mappedAgents)&&scenario.mappedAgents.length>0?'blue':'blue'} onClick={() => onResumeScenario?.({ id: scenario.id, phase: 'agent-mapping' })} sx={{ cursor: 'pointer' }}>
                      {Array.isArray(scenario.mappedAgents)&&scenario.mappedAgents.length>0 ? `Agents ✓` : 'Agents'}
                    </Badge>
                    <Badge variant="light" size="xs" color={Object.keys(scenario.attackSurface || {}).length>0?'orange':'orange'} onClick={() => onResumeScenario?.({ id: scenario.id, phase: 'attack-surface' })} sx={{ cursor: 'pointer' }}>
                      {Object.keys(scenario.attackSurface || {}).length>0 ? 'Attack ✓' : 'Attack'}
                    </Badge>
                    <Badge variant="light" size="xs" color={Object.keys(scenario.impact || {}).length>0?'red':'red'} onClick={() => onResumeScenario?.({ id: scenario.id, phase: 'impact' })} sx={{ cursor: 'pointer' }}>
                      {Object.keys(scenario.impact || {}).length>0 ? 'Impact ✓' : 'Impact'}
                    </Badge>
                    {(() => {
                      const list = securityControls?.[scenario.id] || [];
                      const count = list.filter((c) => ['saved','updated'].includes((c.status||'').toLowerCase())).length;
                      return (
                        <Badge variant="light" size="xs" color="green" onClick={() => onResumeScenario?.({ id: scenario.id, phase: 'action-plan' })} sx={{ cursor: 'pointer' }}>
                          {count > 0 ? `Actions ✓` : 'Actions'}
                        </Badge>
                      );
                    })()}
                  </Group>
                  <Button size="xs" variant="light" onClick={() => onResumeScenario?.(scenario)}>
                    Resume
                  </Button>
                  <ActionIcon
                    size="lg"
                    variant="subtle"
                    color="red"
                    onClick={() => { setPendingDeleteId(scenario.id); setConfirmOpen(true); }}
                  >
                    <IconTrash size={18} />
                  </ActionIcon>
                </Group>
              </Group>
              {/* No details echoing; only chips and actions */}
            </Paper>
          ))}
        </Stack>
      )}

      {/* Add Scenario CTA */}
      <Button variant="subtle" leftSection={<IconPlus size={18} />} onClick={handleOpenNameInput}>
        Add scenario
      </Button>

      {/* Completed (renders items based on persisted status filter) */}
      {statusFilter==='completed' && completedDedup.length > 0 && (
        <Stack gap="md" mt="md">
          {completedDedup.map((scenario) => (
            <CompletedScenarioCard
              key={scenario.id}
              scenario={scenario}
              onRemove={onRemoveScenario}
              onNavigatePhase={(sid, phase) => onResumeScenario?.({ id: sid, phase })}
              onEdit={(sid) => onResumeScenario?.({ id: sid, phase: 'attributes' })}
            />
          ))}
        </Stack>
      )}

      <Modal opened={confirmOpen} onClose={() => setConfirmOpen(false)} title="Delete scenario?" centered>
        <Stack gap="sm">
          <Text size="sm">This will remove the scenario and its saved controls. This action cannot be undone.</Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => { setConfirmOpen(false); setPendingDeleteId(null); }}>Cancel</Button>
            <Button color="red" onClick={() => { const id = pendingDeleteId; setConfirmOpen(false); setPendingDeleteId(null); if (id) onRemoveScenario?.(id); }}>Delete</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}